import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface EducationMetrics {
  studentsUsedAi: number
  avgPromptsPerStudent: number
  pctFollowUp: number
  pctSources: number
  pctRevised: number
  copyPasteRiskFlags: number
  teacherTimeSavedSec: number
  teacherSatisfactionAvg: number | null
  studentSatisfactionAvg: number | null
  qualityImprovementAvg: number | null
}

export interface EducationOrgBreakdown extends EducationMetrics {
  organizationId: number
  organizationName: string
}

export interface EducationProjectBreakdown extends EducationMetrics {
  projectId: number
  projectName: string
  organizationId: number
  organizationName: string
}

export interface EducationOrgOption {
  id: number
  name: string
}

export interface EducationProjectOption {
  id: number
  name: string
  organizationId: number
}

export interface EducationAnalyticsFilters {
  dateFrom: string
  dateTo: string
  organizationId: number | null
  projectId: number | null
}

export interface EducationFeedbackRow {
  id: number
  actorRole: string
  score: number | null
  comment: string | null
  promptKind: string | null
  organizationName: string | null
  projectName: string | null
  userName: string | null
  userEmail: string | null
  deliverableTitle: string | null
  submittedAt: string | null
}

export interface EducationAnalyticsData {
  summary: EducationMetrics
  byOrganization: EducationOrgBreakdown[]
  byProject: EducationProjectBreakdown[]
  organizations: EducationOrgOption[]
  projects: EducationProjectOption[]
  filters: EducationAnalyticsFilters
  recentFeedback?: EducationFeedbackRow[]
  note?: string
}

export interface EducationAnalyticsParams {
  dateFrom?: string
  dateTo?: string
  organizationId?: number | string
  projectId?: number | string
}

export const educationApi = createApi({
  reducerPath: 'educationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['EducationAnalytics'],
  endpoints: (builder) => ({
    getEducationAnalytics: builder.query<
      { success: boolean; data: EducationAnalyticsData },
      EducationAnalyticsParams
    >({
      query: (params) => ({
        url: '/education',
        params,
      }),
      providesTags: ['EducationAnalytics'],
    }),
  }),
})

export const { useGetEducationAnalyticsQuery } = educationApi
